"use client";

import { useCallback, useMemo, useState } from "react";
import { type Situation } from "@/generated/fastapi";
import {
  useConfirmThoughtsMutation,
  useThoughtRefresh,
} from "@/features/thoughts";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import { useUserChoices } from "@/state/globalStore";
import type { ThoughtReassignData } from "@/state/globalStoreSlices/userChoices";

type UseThoughtAnchorWorkflowOptions = {
  validSituationIds?: string[];
};

export default function useThoughtAnchorWorkflow(
  items: ThoughtReassignData[],
  options: UseThoughtAnchorWorkflowOptions = {},
) {
  const [movingToSituationId, setMovingToSituationId] = useState<string | null>(
    null,
  );
  const { reassignSelectedThoughts } = useUserChoices();
  const confirmThoughtsMutation = useConfirmThoughtsMutation();
  const { refreshReflectionSurfaces, syncConfirmedThoughtMutation } =
    useThoughtRefresh();

  const thoughtIds = useMemo(
    () => items.map((item) => item.objectID),
    [items],
  );

  const sourceSituationIds = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => item.situation_id)
            .filter((situationId): situationId is string =>
              Boolean(situationId),
            ),
        ),
      ),
    [items],
  );
  const invalidSourceSituationIds = useMemo(() => {
    if (!options.validSituationIds) {
      return [];
    }

    const validSituationIdSet = new Set(options.validSituationIds);

    return sourceSituationIds.filter(
      (situationId) => !validSituationIdSet.has(situationId),
    );
  }, [options.validSituationIds, sourceSituationIds]);
  const hasUnassignedSource = useMemo(
    () => items.some((item) => !item.situation_id),
    [items],
  );
  const hasNeedsAnchorSource =
    hasUnassignedSource || invalidSourceSituationIds.length > 0;

  const sourcePrototypes = useMemo(
    () => Array.from(new Set(items.map((item) => item.prototype))),
    [items],
  );

  const canMoveToSituation = useCallback(
    (situation: Situation) =>
      sourcePrototypes.every((prototype) =>
        situation.prototypes.includes(prototype),
      ),
    [sourcePrototypes],
  );

  const isCurrentDestination = useCallback(
    (situationId: string) =>
      items.length > 0 &&
      items.every((item) => item.situation_id === situationId),
    [items],
  );

  const moveThoughtsToSituation = useCallback(
    async (assignSituationId: string) => {
      setMovingToSituationId(assignSituationId);

      try {
        const response = await confirmThoughtsMutation.mutateAsync({
          assign_situation_id: assignSituationId,
          reset_reflections: true,
          thought_ids: thoughtIds,
        });

        if (response.status === "success" || response.status === "pending") {
          reassignSelectedThoughts(items, assignSituationId);
          syncConfirmedThoughtMutation(sourcePrototypes);
          refreshReflectionSurfaces();

          if (response.status === "pending") {
            showWarningToast(
              response.message ||
                "Thoughts anchored, but search is still catching up.",
            );
          } else {
            showSuccessToast("Thoughts anchored");
          }

          return true;
        }

        showErrorToast(response.message || "Failed to anchor thoughts");
        return false;
      } catch {
        showErrorToast("Failed to anchor thoughts");
        return false;
      } finally {
        setMovingToSituationId(null);
      }
    },
    [
      confirmThoughtsMutation,
      refreshReflectionSurfaces,
      syncConfirmedThoughtMutation,
      items,
      reassignSelectedThoughts,
      thoughtIds,
      sourcePrototypes,
    ],
  );

  return {
    canMoveToSituation,
    isCurrentDestination,
    isMoving: confirmThoughtsMutation.isPending,
    moveThoughtsToSituation,
    movingToSituationId,
    hasUnassignedSource,
    hasNeedsAnchorSource,
    invalidSourceSituationIds,
    sourceSituationIds,
    sourcePrototypes,
    thoughtIds,
  };
}
